export function isTrueFlag(value: unknown): boolean {
  return value === true;
}

export function adjacencyTone(value: unknown): "danger" | "warn" | "info" | "muted" {
  if (value === "direct") return "danger";
  if (value === "overlapping") return "warn";
  if (value === "adjacent") return "info";
  return "muted";
}

export function asText(value: unknown): string | null {
  if (typeof value === "boolean") return value ? "Yes" : null;
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

export function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => asText(item)).filter((item): item is string => Boolean(item));
}

/** Strings from a string, string[], or objects with name / category_name / title / label. */
export function asDisplayList(value: unknown): string[] {
  if (value == null || value === "") return [];
  if (typeof value === "string") {
    const text = value.trim();
    return text ? [text] : [];
  }
  if (Array.isArray(value)) {
    const out: string[] = [];
    for (const item of value) {
      for (const part of asDisplayList(item)) {
        if (!out.includes(part)) out.push(part);
      }
    }
    return out;
  }
  if (typeof value === "object") {
    const rec = value as Record<string, unknown>;
    const named =
      asText(rec.name) ?? asText(rec.category_name) ?? asText(rec.title) ?? asText(rec.label);
    return named ? [named] : [];
  }
  const text = asText(value);
  return text ? [text] : [];
}

export type TechStackEntry = {
  name: string;
  category: string | null;
  role: string | null;
  confidence: string | null;
  reviewed: boolean | null;
};

export type TechStackView = {
  softwareUsed: TechStackEntry[];
  systemsUsed: TechStackEntry[];
  unverified: boolean;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function parseTechStackEntry(value: unknown): TechStackEntry | null {
  if (typeof value === "string") {
    const name = value.trim();
    return name ? { name, category: null, role: null, confidence: null, reviewed: null } : null;
  }
  const rec = asRecord(value);
  if (!rec) return null;
  const name = asText(rec.name) ?? asText(rec.product) ?? asText(rec.title) ?? asText(rec.label);
  if (!name) return null;
  return {
    name,
    category: asText(rec.category),
    role: asText(rec.role),
    confidence: asText(rec.confidence),
    reviewed: typeof rec.reviewed === "boolean" ? rec.reviewed : null,
  };
}

function parseTechStackList(value: unknown): TechStackEntry[] {
  if (value == null || value === "") return [];
  const items = Array.isArray(value) ? value : [value];
  const out: TechStackEntry[] = [];
  for (const item of items) {
    const parsed = parseTechStackEntry(item);
    if (parsed) out.push(parsed);
  }
  return out;
}

function profileRecord(value: unknown): Record<string, unknown> {
  if (typeof value === "string") {
    try {
      return asRecord(JSON.parse(value)) ?? {};
    } catch {
      return {};
    }
  }
  return asRecord(value) ?? {};
}

/** Account-only tech stack from account_get / research_account_get profile. */
export function parseTechStackView(profile: unknown, nestedProfile?: unknown): TechStackView {
  const top = profileRecord(profile);
  const nested = profileRecord(nestedProfile);
  const blobRaw = nested.tech_stack ?? top.tech_stack;
  const blob = profileRecord(blobRaw);
  const softwareUsed = parseTechStackList(blob.software_used ?? nested.software_used ?? top.software_used);
  const systemsUsed = parseTechStackList(blob.systems_used ?? nested.systems_used ?? top.systems_used);
  if (!softwareUsed.length && !systemsUsed.length) {
    if (typeof blobRaw === "string") {
      return {
        softwareUsed: parseTechStackList(blobRaw.split(/[,;\n]+/)),
        systemsUsed: [],
        unverified: false,
      };
    }
    if (Array.isArray(blobRaw)) {
      return { softwareUsed: parseTechStackList(blobRaw), systemsUsed: [], unverified: false };
    }
  }
  const reviewed =
    typeof blob.reviewed === "boolean"
      ? blob.reviewed
      : typeof nested.reviewed === "boolean"
        ? nested.reviewed
        : typeof top.reviewed === "boolean"
          ? top.reviewed
          : null;
  return {
    softwareUsed,
    systemsUsed,
    unverified: reviewed === false,
  };
}

export function httpsUrl(value: unknown): string | null {
  const text = asText(value);
  if (!text) return null;
  if (/^https?:\/\//i.test(text)) return text;
  return `https://${text}`;
}
